// Tiny inline-SVG sparkline. No charting lib — keeps the bundle slim
// and the look consistent with the rest of the neon HUD aesthetic.
//
// `values` is an array of numbers (left→right chronological). The line
// is normalised to the viewBox; flat data renders as a centred horizontal.
// Last point gets a small accent dot so the latest day stays legible
// even when the sparkline is short (7-day window).
export function Sparkline({
  values = [],
  width = 140,
  height = 38,
  color = "#00ffff",
  ariaLabel,
  testId,
}) {
  if (!Array.isArray(values) || values.length === 0) {
    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel || "No data"}
        data-testid={testId}
      />
    );
  }
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(1, max - min);
  const stepX = values.length > 1 ? width / (values.length - 1) : width;
  const padTop = 4;
  const padBot = 4;
  const usable = height - padTop - padBot;

  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = padTop + (1 - (v - min) / span) * usable;
    return [x, y];
  });
  const linePath = points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");
  // Filled area under the line — gives the sparkline visual weight even
  // when the values are tiny single digits.
  const areaPath =
    `${linePath} L${(width).toFixed(1)},${(height - padBot).toFixed(1)} L0,${(height - padBot).toFixed(1)} Z`;

  const last = points[points.length - 1];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel || `Sparkline last value ${values[values.length - 1]}`}
      data-testid={testId}
    >
      <defs>
        <linearGradient id={`spark-grad-${testId}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#spark-grad-${testId})`} />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        style={{ filter: `drop-shadow(0 0 3px ${color}80)` }}
      />
      <circle cx={last[0]} cy={last[1]} r="2.2" fill={color} />
    </svg>
  );
}
